import React from 'react';
import {
  Grid,
  Typography,
  Divider,
  Card,
  CardContent,
  Box,
  Skeleton,
} from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIntlInfo } from '../../../endpoints/chaptersTs';
import { InternationalChapterInfoResponse } from '../../../types/chapters';

const IntlInfo: React.FC = () => {
  const { data: intlInfo, isFetching } =
    useApiGet<InternationalChapterInfoResponse>(['intl-info'], getIntlInfo, {
      enabled: true,
      retry: 1,
    });

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  if (isFetching) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">P.E.O. International</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  <Skeleton variant="text" width="100%" height={60} />
                </Typography>
                <Grid container spacing={3}>
                  {[1, 2, 3, 4].map((item) => (
                    <Grid item xs={12} md={3} key={item}>
                      <Box textAlign="center">
                        <Skeleton variant="text" width="80%" height={40} />
                        <Skeleton variant="text" width="60%" height={20} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!intlInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Unable to load international information
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">P.E.O. International</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
              >
                P.E.O. was founded on January 21, 1869, by seven students at
                Iowa Wesleyan University in Mount Pleasant, Iowa. This circle of
                kindred spirits – bonded by their enthusiasm for women's
                opportunities – eventually expanded to include women off campus
                as well.
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center', fontStyle: 'italic' }}
              >
                P.E.O.—Where women motivate, educate and celebrate women.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Statistics</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(intlInfo.active_members)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Members
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(intlInfo.active_chapters)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Chapters
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(intlInfo.initiates)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      New Initiates
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      (This Membership Year)
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(intlInfo.reinstatements)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reinstatements
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      (This Membership Year)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IntlInfo;
